import { ReactElement, useCallback } from 'react';

import { getTitulationData } from '../../../../../api/academicHistoryRequests';
import { useTranslation } from 'react-i18next';
import { TitulationData } from '../types';
import { createFetchContext } from '../../../../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../../../../components/Info/DisplayError';
import Loading from '../../../../../../../../components/Info/Loading';
import { TranslationsKeys } from '../../../../../../../../locales/translations';

export interface TitulationLoaderProps<T> {
  studyPlanEnrollmentId: string;
  children: (props: { data: T }) => ReactElement;
}

const { FetchProvider, FetchConsumer } = createFetchContext<
  { studyPlanEnrollmentId: string },
  TitulationData
>();

export const BaseLoaderConsumer = FetchConsumer;

export function TitulationLoader({
  studyPlanEnrollmentId,
  children,
}: TitulationLoaderProps<TitulationData>) {
  const { t } = useTranslation(TranslationsKeys.STUDENT_FILE_ACADEMIC_HISTORY);
  const request = useCallback(() => {
    return getTitulationData(studyPlanEnrollmentId);
  }, [studyPlanEnrollmentId]);
  return (
    <FetchProvider
      request={request}
      defaultQuery={{ studyPlanEnrollmentId }}
      fetchImmediately
    >
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return (
              <div className="mx-3">
                <Loading insideCard />
              </div>
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (data.student.notGraduated) {
            return (
              <DisplayError
                insideCard
                title={t('tabs.titulation.notGraduatedStudent.title')}
                textBody={t('tabs.titulation.notGraduatedStudent.body')}
              />
            );
          }

          return children({ data });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
